import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Cpu, Zap, Database, Filter, TrendingUp } from "lucide-react";

const MEAuxEngineLoad = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      category: "Source Data Fields",
      items: [
        {
          name: "maxCapacityLoad",
          source: "VesselAuxEngineData",
          description: "Designed maximum power output capacity",
          calculation: "Direct value from database field"
        },
        {
          name: "reportedCapacityLoad",
          source: "VesselAuxEngineData",
          description: "Actual power output being generated",
          calculation: "Direct value from database field"
        },
        {
          name: "averageReportedLoad",
          source: "VesselAuxEngineData",
          description: "Mean power output over measurement period",
          calculation: "Direct value from database field"
        },
        {
          name: "engineId",
          source: "VesselAuxEngineData",
          description: "Auxiliary engine identifier",
          calculation: "Regex pattern matching for pipe-separated values"
        }
      ]
    },
    {
      category: "Calculated Fields",
      items: [
        {
          name: "loadValue",
          source: "Calculated ratio",
          description: "Load factor ratio",
          calculation: "maxCapacityLoad ÷ reportedCapacityLoad"
        },
        {
          name: "maxCapacity",
          source: "Returned as-is",
          description: "Maximum power capacity",
          calculation: "Direct value from maxCapacityLoad field"
        }
      ]
    }
  ];

  const calculations = [
    {
      title: "Load Factor Calculation",
      formula: "maxCapacityLoad ÷ reportedCapacityLoad",
      description: "Ratio of maximum capacity to actual reported load",
      parameters: ["maxCapacityLoad > 0 required", "reportedCapacityLoad > 0 required", "Zero if either is zero"]
    }
  ];

  const responseStructure = [
    {
      field: "loadValue",
      type: "number",
      format: "dimensionless ratio",
      description: "Load factor (max capacity ÷ reported load)",
      example: "1.25"
    },
    {
      field: "maxCapacity",
      type: "number",
      format: "kilowatts (kW)",
      description: "Maximum designed power capacity",
      example: "2500"
    },
    {
      field: "unit",
      type: "string",
      format: "text",
      description: "Measurement unit for capacity",
      example: "kW"
    }
  ];

  const dataRetrievalSteps = [
    {
      step: 1,
      description: "Validate Vessel ID and Engine ID",
      details: "Check that both required parameters are provided"
    },
    {
      step: 2,
      description: "Determine Date Range",
      details: "Use voyage dates or last 7 days default"
    },
    {
      step: 3,
      description: "Filter Engine Data",
      details: "Apply regex pattern to match specific auxiliary engine"
    },
    {
      step: 4,
      description: "Retrieve Latest Record",
      details: "Get most recent data sorted by recordedAt descending"
    },
    {
      step: 5,
      description: "Calculate Load Factor",
      details: "Compute ratio if both capacity values are positive"
    },
    {
      step: 6,
      description: "Return Values",
      details: "Return calculated load factor and maximum capacity"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <Cpu className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Auxiliary Engine Load Factor Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                Load factor calculation using latest auxiliary engine data
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            
            {/* Data Sources */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Data Sources & Fields
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dataSources.map((source, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-3">{source.category}</h4>
                    <div className="space-y-3">
                      {source.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-gray-800">{item.name}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {item.source}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{item.description}</p>
                          <p className="text-xs text-gray-500">Calculation: {item.calculation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculations */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Core Calculations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {calculations.map((calc, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Zap className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{calc.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{calc.description}</p>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                      <p className="text-sm font-mono bg-gray-50 p-2 rounded border">
                        {calc.formula}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Parameters:</p>
                      <div className="flex flex-wrap gap-1">
                        {calc.parameters.map((param, paramIdx) => (
                          <span key={paramIdx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {param}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MEAuxEngineLoad;